import type {
  LayMinesAction,
  MoveAction,
  OblivionMineAction,
  OblivionMineMine,
  OblivionMineState,
  OblivionMineTreasure,
} from "../types";
import { OBLIVION_MINE_STARTS, OBLIVION_MINE_TREASURES_TO_WIN } from "../types";
import {
  checkWinner,
  findArmedOpponentMineIndex,
  getOpponentPlayerId,
  uncollectedTreasureIndexAt,
} from "../utils";

export function applyLayMines(
  state: OblivionMineState,
  action: LayMinesAction,
): OblivionMineState {
  const newMines: OblivionMineMine[] = action.cells.map((cell) => ({
    owner: action.playerId,
    row: cell.row,
    col: cell.col,
    triggered: false,
  }));
  const minesLaid = { ...state.minesLaid, [action.playerId]: true };
  const bothLaid = minesLaid.p1 && minesLaid.p2;

  return {
    ...state,
    mines: [...state.mines, ...newMines],
    minesLaid,
    // Sequential commit: once both have laid, the move phase starts with the
    // first player; otherwise it is the opponent's turn to lay.
    phase: bothLaid ? "move" : "lay-mines",
    currentPlayerId: bothLaid
      ? state.firstPlayerId
      : getOpponentPlayerId(action.playerId),
    moveNumber: state.moveNumber + 1,
    lastAction: action,
    lastHit: null,
  };
}

export function applyMove(
  state: OblivionMineState,
  action: MoveAction,
): OblivionMineState {
  const mover = action.playerId;
  const opponent = getOpponentPlayerId(mover);
  const { to } = action;

  const hitIndex = findArmedOpponentMineIndex(
    state.mines,
    to.row,
    to.col,
    mover,
  );

  if (hitIndex >= 0) {
    // Hit: reveal+disarm the mine, knock the piece back to its start, lose turn.
    const mines = state.mines.map((mine, index) =>
      index === hitIndex ? { ...mine, triggered: true } : mine,
    );
    return {
      ...state,
      mines,
      positions: { ...state.positions, [mover]: OBLIVION_MINE_STARTS[mover] },
      currentPlayerId: opponent,
      moveNumber: state.moveNumber + 1,
      lastAction: action,
      lastHit: { playerId: mover, row: to.row, col: to.col, owner: opponent },
    };
  }

  // Safe move: advance the piece, collect a treasure if present, check the win.
  const treasureIndex = uncollectedTreasureIndexAt(
    state.treasures,
    to.row,
    to.col,
  );
  let treasures: readonly OblivionMineTreasure[] = state.treasures;
  let scores = state.scores;
  if (treasureIndex >= 0) {
    treasures = state.treasures.map((treasure, index) =>
      index === treasureIndex ? { ...treasure, collectedBy: mover } : treasure,
    );
    scores = { ...state.scores, [mover]: state.scores[mover] + 1 };
  }

  const winnerId = checkWinner(scores, OBLIVION_MINE_TREASURES_TO_WIN);

  return {
    ...state,
    positions: { ...state.positions, [mover]: to },
    treasures,
    scores,
    phase: winnerId ? "game-over" : "move",
    currentPlayerId: winnerId ? mover : opponent,
    moveNumber: state.moveNumber + 1,
    lastAction: action,
    lastHit: null,
    winnerId,
  };
}

export function applyAction(
  state: OblivionMineState,
  action: OblivionMineAction,
): OblivionMineState {
  switch (action.type) {
    case "LayMines":
      return applyLayMines(state, action);
    case "Move":
      return applyMove(state, action);
  }
}
